import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenSessionRequest } from '../../../hooks/useApi';
import './StartSessionModal.css';

const UploadIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 26V14M20 14L15 19M20 14L25 19" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 30H30" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" stroke="#6B7280" strokeWidth="1.5"/>
    <path d="M8 7V11M8 5V5.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const StartSessionModal = ({isOpen, onClose, inventoryId, posId}) =>
{
  const navigate = useNavigate();
  const [areaName, setAreaName] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [{ loading }, openSession] = useOpenSessionRequest(posId);

  if (!isOpen) return null;

  const handleClose = () =>
  {
    setAreaName('');
    setFile(null);
    setError(null);
    onClose();
  };

  const handleFileSelect = (selectedFile) =>
  {
    if (selectedFile)
    {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDragOver = (e) =>
  {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) =>
  {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleInputChange = (e) =>
  {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleFileSelect(selectedFile);
  };

  const handleSubmit = async () =>
  {
    if (!file)
    {
      setError('Please select a file to upload.');
      return;
    }
    setError(null);
    const openResult = await openSession({ areaName: areaName.trim(), inventoryId, file });
    if (openResult.error)
    {
      setError(openResult.error);
      return;
    }

    const { sessionId, sessionScans } = openResult.data;

    handleClose();
    navigate(`/inventory-management/${inventoryId}/session/${sessionId}`, {
      state: { sessionScans: sessionScans || [], areaName: areaName.trim() || null, posId }
    });
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content start-session-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Start Session</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="start-session-error">
              {error}
            </div>
          )}

          <div className="start-session-field">
            <label className="start-session-label">Area Name</label>
            <input
              type="text"
              className="start-session-input"
              placeholder="Type area name"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
            />
          </div>

          <div
            className={`start-session-dropzone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="start-session-file-input"
              onChange={handleInputChange}
            />
            <UploadIcon/>
            {file ? (
              <p className="start-session-drop-text">
                <strong>{file.name}</strong>
              </p>
            ) : (
              <>
                <p className="start-session-drop-text">
                  Drop your file here or <span className="start-session-browse-link">browse</span>
                </p>
                <p className="start-session-drop-hint">Supports: CSV files</p>
              </>
            )}
          </div>

          <div className="start-session-requirements">
            <InfoIcon/>
            <div>
              <p className="start-session-req-title">File Format Requirements</p>
              <p className="start-session-req-text">Your file should contain the following columns: Barcode and Quantity.</p>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading || !file}>
            {loading ? 'Uploading...' : 'Add Scans'}
          </button>
        </div>
      </div>
    </div>
  );
};
